import {
  Button,
  Flex,
  Heading,
  IconArrowOpenDownLine,
  IconArrowOpenEndLine,
  IconButton,
  IconEditLine,
  IconTrashLine,
  List,
  Modal,
  Text,
  TextArea,
  ToggleButton,
  Tooltip,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { ActionService } from "../../services/ActionService";
import { ExternalService } from "../../services/ExternalService";
import { ReflectionService } from "../../services/ReflectionService";
import { UserDetailContext } from "../../utils/Context";
import { ActionEditPanel } from "../Action/ActionEditPanel";
import { CommonModal } from "../CommonModal";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../DialogYesNo";

export const ContextReflections = ({
  contextId,
  contextName,
  externalUserName,
}) => {
  const showNotification = useNotification();
  const userDetails = useContext(UserDetailContext);
  const [keyList, setKeyList] = useState([]);
  const [openReflectionModal, setOpenReflectionModal] = useState(false);
  const [reflectionList, setReflectionList] = useState([]);
  const [reflectionText, setReflectionText] = useState("");
  const [loadReflections, setLoadReflections] = useState(false);
  const [savingReflection, setSavingReflection] = useState(false);
  const [currentReflection, setCurrentReflection] = useState(null);
  const [editingReflection, setEditingReflection] = useState(null);
  const [reflectonToDelete, setReflectionToDelete] = useState(null);
  const [openActionModal, setOpenActionModal] = useState(false);
  const [actionMap, setActionMap] = useState({});
  const [actionToDelete, setActionToDelete] = useState(null);
  const [editingAction, setEditingAction] = useState(null);

  const handleClose = () => {
    setCurrentReflection(null);
    setEditingAction(null);
  };

  useEffect(() => {
    setLoadReflections(true);

    const getReflections = externalUserName
      ? ExternalService.getExternalReflectionsForContext
      : ReflectionService.getReflectionsForContext;

    getReflections(contextId)
      .then((reflections) => {
        setReflectionList(reflections);
      })
      .catch(() => {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("reflections.reflections"),
          }),
          ERROR
        );
      })
      .finally(() => setLoadReflections(false));
  }, [contextId, showNotification]);

  const handleDetails = (ref) => {
    if (keyList.includes(ref.id)) {
      const newDetails = keyList.filter((value) => value !== ref.id);
      setKeyList(newDetails);
    } else {
      setKeyList((prevKeyList) => [...prevKeyList, ref.id]);

      const getActions = externalUserName
        ? ExternalService.getExternalActionsForContext
        : ActionService.getActionsForContext;

      getActions(ref.id)
        .then((actions) => {
          setActionMap((prevMap) => ({ ...prevMap, [ref.id]: actions }));
        })
        .catch(() => {
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("actions.actions"),
            }),
            ERROR
          );
        });
    }
  };

  const handleAddReflection = () => {
    setSavingReflection(true);
    const addReflection = externalUserName
      ? ExternalService.addExternalReflectionForContext
      : ReflectionService.addReflectionForContext;
    const name = externalUserName ? externalUserName : userDetails.name;

    addReflection(reflectionText, name, contextId)
      .then((newReflection) => {
        reflectionList.unshift(newReflection);
        setOpenReflectionModal(false);
      })
      .catch(() => {
        showNotification(
          t("notifications.general.createError", {
            obj: t("reflections.reflection"),
          }),
          ERROR
        );
      })
      .finally(() => setSavingReflection(false));
  };

  const handleEditReflection = (refToEdit) => {
    setOpenReflectionModal(true);
    setEditingReflection(refToEdit);
    setReflectionText(refToEdit.text);
  };

  const handleUpdateReflection = () => {
    setSavingReflection(true);
    const updatedReflection = { ...editingReflection, text: reflectionText };
    const updateReflection = externalUserName
      ? ExternalService.updateExternalReflection
      : ReflectionService.updateReflection;

    updateReflection(updatedReflection)
      .then((newReflection) => {
        const updatedList = [...reflectionList];
        const index = updatedList.findIndex(
          (ref) => ref.id === newReflection.id
        );
        updatedList[index] = newReflection;
        setReflectionList(updatedList);
        setOpenReflectionModal(false);
      })
      .catch(() => {
        showNotification(
          t("notifications.general.updateError", {
            obj: t("reflections.reflection"),
          }),
          ERROR
        );
      })
      .finally(() => setSavingReflection(false));
  };

  const handleRemoveReflection = async () => {
    const deleteReflection = externalUserName
      ? ExternalService.deleteExternalReflection
      : ReflectionService.deleteReflection;

    await deleteReflection(reflectonToDelete.id)
      .then(() => {
        setReflectionList((prevList) =>
          prevList.filter((item) => item.id !== reflectonToDelete.id)
        );
        setReflectionToDelete(null);
      })
      .catch(() => {
        showNotification(
          t("notifications.general.deleteError", {
            obj: t("reflections.reflection"),
          }),
          ERROR
        );
      });
  };

  const handleAddAction = (reflection) => {
    setCurrentReflection(reflection);
    setOpenActionModal(true);
  };

  const handleRemoveAction = async () => {
    const deleteAction = externalUserName
      ? ExternalService.deleteExternalAction
      : ActionService.deleteAction;

    await deleteAction(actionToDelete.id)
      .then(() => {
        const newActionMap = { ...actionMap };
        newActionMap[currentReflection.id] = newActionMap[
          currentReflection.id
        ].filter((item) => item.id !== actionToDelete.id);
        setActionMap(newActionMap);
        setActionToDelete(null);
        setCurrentReflection(null);
      })
      .catch(() => {
        showNotification(
          t("notifications.general.deleteError", { obj: t("actions.action") }),
          ERROR
        );
      });
  };

  const handleReflectionModalClose = () => {
    setReflectionText("");
    setEditingReflection(null);
  };

  const addReflectionBody = () => {
    return (
      <Modal.Body>
        <Flex wrap="wrap">
          <Flex.Item width="100%" margin="0 0 small 0">
            <TextArea
              label={t("reflections.reflectionDesc")}
              value={reflectionText}
              onChange={(event) => setReflectionText(event.target.value)}
            />
          </Flex.Item>
          <Flex.Item width="100%" margin="0 0 0 0">
            <Text weight="bold">{t("reflections.relatesTo")} </Text>
            <Text weight="bold" color="brand">
              {contextName}
            </Text>
          </Flex.Item>
        </Flex>
      </Modal.Body>
    );
  };

  const showListOfActions = (reflection) => {
    if (actionMap[reflection.id] !== undefined) {
      if (actionMap[reflection.id].length > 0) {
        return (
          <List size="large" margin="0 0 small 0">
            {actionMap[reflection.id].map((item, key) => (
              <List.Item key={key}>
                <Flex justifyItems="space-between">
                  <Flex.Item shouldShrink>
                    <Text as="p" size="small">
                      {item.text}
                    </Text>
                  </Flex.Item>
                  <Flex.Item align="start">
                    <Tooltip
                      color="primary-inverse"
                      renderTip={t("actions.editAction")}
                    >
                      <IconButton
                        screenReaderLabel={t("actions.editAction")}
                        renderIcon={IconEditLine}
                        size="small"
                        margin="0 small 0 0"
                        onClick={() => {
                          setOpenActionModal(true);
                          setEditingAction(item);
                          setCurrentReflection(reflection);
                        }}
                      />
                    </Tooltip>
                    <Tooltip
                      color="primary-inverse"
                      renderTip={t("actions.deleteAction")}
                    >
                      <IconButton
                        screenReaderLabel={t("actions.deleteAction")}
                        renderIcon={IconTrashLine}
                        size="small"
                        onClick={() => {
                          setActionToDelete(item);
                          setCurrentReflection(reflection);
                        }}
                      />
                    </Tooltip>
                  </Flex.Item>
                </Flex>
              </List.Item>
            ))}
          </List>
        );
      } else {
        return (
          <Heading level="h5" as="h4">
            {t("actions.noActions")}
          </Heading>
        );
      }
    } else {
      return <Skeleton count={3} />;
    }
  };

  function getContent(reflections) {
    return loadReflections ? (
      <Skeleton
        count={3}
        style={{ height: 64, borderRadius: 5, marginBottom: 10 }}
      />
    ) : reflections.length === 0 ? (
      <Heading level="h5" as="h4">
        {t("reflections.noReflections")}
      </Heading>
    ) : (
      reflections.map((ref) => (
        <Flex key={ref.id} margin="0 0 medium 0">
          <Flex.Item margin="0 small 0 0" align="start">
            <ToggleButton
              size="small"
              status={keyList.includes(ref.id) ? "pressed" : "unpressed"}
              renderTooltipContent={t("reflections.showReflections")}
              screenReaderLabel={t("reflections.showReflections")}
              renderIcon={
                keyList.includes(ref.id)
                  ? IconArrowOpenDownLine
                  : IconArrowOpenEndLine
              }
              onClick={() => handleDetails(ref)}
            />
          </Flex.Item>
          <Flex.Item shouldShrink shouldGrow>
            <Flex alignItems="start">
              <Flex.Item shouldShrink shouldGrow>
                <Text as="p" size="small">
                  {ref.text}
                </Text>
              </Flex.Item>
              <Flex.Item>
                <Tooltip
                  color="primary-inverse"
                  renderTip={t("reflections.editRef")}
                >
                  <IconButton
                    screenReaderLabel={t("reflections.editRef")}
                    renderIcon={IconEditLine}
                    size="small"
                    margin="0 small 0 0"
                    onClick={() => handleEditReflection(ref)}
                  />
                </Tooltip>
              </Flex.Item>
              <Flex.Item>
                <Tooltip
                  color="primary-inverse"
                  renderTip={t("reflections.deleteRef")}
                >
                  <IconButton
                    screenReaderLabel={t("reflections.deleteRef")}
                    renderIcon={IconTrashLine}
                    size="small"
                    onClick={() => setReflectionToDelete(ref)}
                  />
                </Tooltip>
              </Flex.Item>
            </Flex>
            <Text as="p" fontStyle="italic" size="small">
              {ref.author} - {new Date(ref.timestamp + "Z").toLocaleString()}
            </Text>
            {keyList.includes(ref.id) && (
              <div style={{ marginTop: 5, marginLeft: 20 }} key={ref.id}>
                <Flex alignItems="center">
                  <Flex.Item margin="0 small 0 0">
                    <Text as="div" size="small" weight="bold">
                      {t("actions.actions")}
                    </Text>
                  </Flex.Item>
                  <Flex.Item>
                    <Button size="small" onClick={() => handleAddAction(ref)}>
                      {t("actions.addAction")}
                    </Button>
                  </Flex.Item>
                </Flex>
                {showListOfActions(ref)}
              </div>
            )}
          </Flex.Item>
        </Flex>
      ))
    );
  }

  const actionCreated = (newAction) => {
    const newActionMap = { ...actionMap };
    newActionMap[currentReflection.id].push(newAction);
    setActionMap(newActionMap);
    setOpenActionModal(false);
  };

  const actionUpdated = (updatedAction) => {
    const newActionMap = { ...actionMap };
    const index = newActionMap[currentReflection.id].findIndex(
      (item) => item.id === updatedAction.id
    );
    newActionMap[currentReflection.id][index] = updatedAction;
    setActionMap(newActionMap);
    setOpenActionModal(false);
  };

  return (
    <>
      <Flex alignItems="center" margin="xx-small 0 small 0">
        <Flex.Item margin="0 small 0 0">
          <Heading as="h3" level="h4">
            {t("reflections.reflections")}
          </Heading>
        </Flex.Item>
        <Flex.Item>
          <Button size="small" onClick={() => setOpenReflectionModal(true)}>
            {t("reflections.addReflection")}
          </Button>
        </Flex.Item>
      </Flex>

      {getContent(reflectionList)}

      <CommonModal
        openModal={openReflectionModal}
        setOpenModal={setOpenReflectionModal}
        handleClose={handleReflectionModalClose}
        label={
          editingReflection
            ? t("reflections.editRef")
            : t("reflections.addReflection")
        }
        size="medium"
        handleSubmit={
          editingReflection ? handleUpdateReflection : handleAddReflection
        }
        body={addReflectionBody()}
        inProgress={savingReflection}
      />

      <ActionEditPanel
        title={editingAction ? t("actions.editAction") : t("actions.addAction")}
        contextId={currentReflection ? currentReflection.id : contextId}
        action={
          editingAction
            ? editingAction
            : {
                action_type: {},
                author: !externalUserName ? userDetails.name : externalUserName,
                reflections: [],
              }
        }
        openModal={openActionModal}
        setOpenModal={setOpenActionModal}
        handleActionUpdated={actionUpdated}
        handleActionCreated={actionCreated}
        attachedReflections={currentReflection ? [currentReflection] : null}
        external={externalUserName}
        handleClose={handleClose}
      />
      <DialogYesNo
        message={t("reflections.deleteConfirm")}
        openStatus={!!reflectonToDelete}
        onClickYes={handleRemoveReflection}
        onClickNo={() => setReflectionToDelete(null)}
      />
      <DialogYesNo
        message={t("actions.deleteConfirm")}
        openStatus={!!actionToDelete}
        onClickYes={handleRemoveAction}
        onClickNo={() => setActionToDelete(null)}
      />
    </>
  );
};
